import React, { Suspense, useCallback, useEffect, useRef, useState } from "react";
import {
	FaClipboardList,
	FaExclamationCircle,
	FaFileAlt,
	FaSave,
	FaUserCheck,
	FaUserEdit,
	FaPlus
} from "react-icons/fa";
import { HiBadgeCheck, HiDocumentReport } from "react-icons/hi";

import { TbFileReport } from "react-icons/tb";
import { useReactToPrint } from "react-to-print";

import ExplanationOfBenefitsGrid from "./explanationOfBenefitsGrid/ExplanationOfBenefitsGrid";

import ExplanationOfBenefitFormPrint from "./explanationOfBenefitFormPrint/ExplanationOfBenefitFormPrint";

import "./explanationOfBenefits.css";

import Remarks from "./remarks/Remarks";

import { api } from "../../../../../../../../utils/api";
import { Backdrop } from "@material-ui/core";
import { SpeedDial, SpeedDialAction } from "@material-ui/lab";
import {
	BOP_PandleFirstBank_logo,
	BrookshireBrothersCircleLogo,
	GroupLogoSample,
	IntercityLogoDarkv2,
	nexcaliberLogo,
	PanhandleFirstBankLogo,
	ProgressiveLabsLogo
} from "../../../../../../../../assets";
import { NEXCALIBER_DETAILS } from "../../../../../../../../constants/nexcaliberDetails";
import { mongoDBDateParserToUSAFormat } from "../../../../../../../../utils/commonFunctions/date";
import { useQuery } from "../../../../../../../../hooks";
import { GROUP_NUMBER } from "../../../../../../../../constants/groups";

const ExplanationOfBenefitsV3: React.FC = (): JSX.Element => {
	const query = useQuery();
	const [claimNo, setClaimNo] = useState<number | null>(null);
	const [claimPrefix, setClaimPrefix] = useState<number | null>(null);
	const [claimSuffix, setClaimSuffix] = useState<number | null>(null);
	const [openSpeedDial, setOpenSpeedDial] = useState<boolean>(false);
	const [eobDetails, setEobDetails] = useState<any>();

	const [groupNumber, setGroupNumber] = useState<number | null>(null);

	const componentRef = useRef(null);
	const handlePrint = useReactToPrint({
		content: () => componentRef.current
	});

	const actions = [{ icon: <FaFileAlt />, name: "Download", handleAction: handlePrint }];

	const handleOpenSpeedDial = () => setOpenSpeedDial(true);
	const handleCloseSpeedDial = () => setOpenSpeedDial(false);

	const getEOBDetails = useCallback(async () => {
		if (claimNo !== null && claimPrefix !== null && claimSuffix !== null) {
			const filter = {
				claim_number: claimNo,
				claim_prefix: claimPrefix,
				claim_suffix: claimSuffix
			};
			const eobDetailsInstance = await api.claim.getEOBDetailsV2(filter);
			setEobDetails(eobDetailsInstance);
		}
	}, [claimPrefix, claimNo, claimSuffix]);

	useEffect(() => {
		if (query && query.get("number") && query.get("pre") && query.get("suffix")) {
			const claim_no = query.get("number");
			const claim_prefix = query.get("pre");
			const claim_suffix = query.get("suffix");
			const groupNumber = query.get("gn");
			setGroupNumber(Number(groupNumber));
			setClaimNo(Number(claim_no));
			setClaimPrefix(Number(claim_prefix));
			setClaimSuffix(Number(claim_suffix));
		}
	}, [query]);

	useEffect(() => {
		getEOBDetails();
	}, [getEOBDetails]);

	// console.log("====>eob details", eobDetails);

	return (
		<div className="EOB-main" id="EOB-main">
			<Backdrop open={openSpeedDial} style={{ zIndex: 1009 }} />
			{eobDetails?.claimantSubscriberDetailsInstance.length > 0 ? (
				<SpeedDial
					ariaLabel="SpeedDial basic example"
					// sx={{ position: "absolute", bottom: 16, right: 16 }}
					style={{
						position: "fixed",
						bottom: 16,
						right: 16
					}}
					icon={<FaPlus />}
					onOpen={handleOpenSpeedDial}
					onClose={handleCloseSpeedDial}
					open={openSpeedDial}
				>
					{actions.map((action: any) => (
						<SpeedDialAction
							key={action.name}
							icon={action.icon}
							tooltipTitle={action.name}
							tooltipOpen
							onClick={action.handleAction}
						/>
					))}
				</SpeedDial>
			) : null}

			<span className="theme-header-1 header">Explanation Of Benefits (EOB)</span>
			<div className="EOB-paper-container">
				{/* ============================= HEADER CONTAINER ================================= */}
				<div className="header-container">
					{/* <div className="group-logo-container">
						<img src={BrookshireBrothersCircleLogo} className="group-logo" alt="" />
					</div> */}
					<div
						className={`group-logo-container ${
							groupNumber === GROUP_NUMBER.BBI_BROOKSHIRE_BROTHERS
								? "group-220"
								: groupNumber === GROUP_NUMBER.ICI_INTERCITY_INVESTMENTS
								? "group-188"
								: groupNumber === GROUP_NUMBER.PANHANDLE_FIRST_BANK
								? "group-225"
								: groupNumber === GROUP_NUMBER.PROGRESSIVE_LABORATORIES
								? "group-300"
								: "group-default"
						}`}
					>
						<img
							src={
								groupNumber === GROUP_NUMBER.BBI_BROOKSHIRE_BROTHERS
									? BrookshireBrothersCircleLogo
									: groupNumber === GROUP_NUMBER.ICI_INTERCITY_INVESTMENTS
									? IntercityLogoDarkv2
									: groupNumber === GROUP_NUMBER.PANHANDLE_FIRST_BANK
									? BOP_PandleFirstBank_logo
									: groupNumber === GROUP_NUMBER.PROGRESSIVE_LABORATORIES
									? ProgressiveLabsLogo
									: BrookshireBrothersCircleLogo
							}
							className="group-logo"
							alt=""
						/>
					</div>
					<div className="nexcaliber-logo-container">
						<img src={nexcaliberLogo} className="nexcaliber-logo" alt="" />
						<div className="nexcaliber-location-details">
							<span>Nexcaliber</span>
							<span>PO BOX 802422, Dalas Texas 75380</span>
							<span>{NEXCALIBER_DETAILS.NEXCALIBER_CONTACT}</span>
						</div>
					</div>
				</div>
				<div className="subheading-main">
					<HiBadgeCheck />
					Explanation of Benefits
				</div>
				{/* ============================= BODY CONTAINER ====================================*/}
				<div className="eob-grid-container">
					<div className="eob-grid-item">
						<div className="category-heading">
							<FaUserCheck />
							<span>Subscriber</span>
						</div>
						<div className="grid-item-fields">
							<span className="field-title">First Name</span>
							<span className="field-value">
								{eobDetails?.claimantSubscriberDetailsInstance[0]?.INSURED_FIRSTNAME || "N/A"}
							</span>
						</div>
						<span className="grid-item-fields">
							<div className="grid-item-fields">
								<span className="field-title">Last Name</span>
								<span className="field-value">
									{eobDetails?.claimantSubscriberDetailsInstance[0]?.INSURED_LASTNAME || "N/A"}
								</span>
							</div>
						</span>
						<span className="grid-item-fields">
							<div className="grid-item-fields">
								<span className="field-title">Address</span>
								<span className="field-value">
									{eobDetails?.claimantSubscriberDetailsInstance[0]?.ADDR1} (
									{eobDetails?.claimantSubscriberDetailsInstance[0]?.STATE}){" "}
									{eobDetails?.claimantSubscriberDetailsInstance[0]?.ZIPCODE}
								</span>
							</div>
						</span>
					</div>
					<div className="eob-grid-item">
						<div className="category-heading">
							<FaUserEdit />
							<span>Claimant</span>
						</div>
						<div className="claimant-container">
							<div className="left-section">
								<div className="grid-item-fields">
									<span className="field-title">Claim For</span>
									<span className="field-value">
										{eobDetails?.claimantSubscriberDetailsInstance[0]?.PATIENT_FIRSTNAME}{" "}
										{eobDetails?.claimantSubscriberDetailsInstance[0]?.PATIENT_LASTNAME}
									</span>
								</div>

								<div className="grid-item-fields">
									<span className="field-title">SSN</span>
									<span className="field-value">
										{eobDetails?.claimantSubscriberDetailsInstance[0]?.SSN || "N/A"}
									</span>
								</div>

								<div className="grid-item-fields">
									<span className="field-title">PPO No :</span>
									<span className="field-value">
										{eobDetails?.claimantSubscriberDetailsInstance[0]?.PPONO || "N/A"}
									</span>
								</div>

								<div className="grid-item-fields">
									<span className="field-title">Claim Number :</span>
									<span className="field-value">
										{eobDetails?.claimantSubscriberDetailsInstance[0]?.CLMNO || "N/A"}
									</span>
								</div>
							</div>
							<div className="right-section">
								<div className="grid-item-fields">
									<span className="field-title">Provider Check :</span>
									<span className="field-value">
										{eobDetails?.claimantSubscriberDetailsInstance[0]?.PRVCKNO || "N/A"}
									</span>
								</div>

								<div className="grid-item-fields">
									<span className="field-title">Paid Date :</span>
									<span className="field-value">
										{eobDetails?.claimantSubscriberDetailsInstance[0]?.PDDT
											? mongoDBDateParserToUSAFormat(
													eobDetails?.claimantSubscriberDetailsInstance[0]?.PDDT
											  )
											: "N/A"}
									</span>
								</div>

								<div className="grid-item-fields">
									<span className="field-title">PPO Name :</span>
									<span className="field-value">
										{eobDetails?.claimantSubscriberDetailsInstance[0]?.PPO_NAME || "N/A"}
									</span>
								</div>
							</div>
						</div>
					</div>
					<div className="eob-grid-item">
						<div className="category-heading">
							<FaClipboardList />
							<span>Provider Details</span>
						</div>
						<span>
							{eobDetails?.claimantSubscriberDetailsInstance[0]?.PRVNO} -{" "}
							{eobDetails?.claimantSubscriberDetailsInstance[0]?.IDSUFFIX}
						</span>
						<span>{eobDetails?.claimantSubscriberDetailsInstance[0]?.NAME}</span>
						<span>
							{eobDetails?.claimantSubscriberDetailsInstance[0]?.ADDR1_1} (
							{eobDetails?.claimantSubscriberDetailsInstance[0]?.STATE_1}){" "}
							{eobDetails?.claimantSubscriberDetailsInstance[0]?.ZIPCODE_1}
						</span>
						{/* <span>{eobDetails?.claimantSubscriberDetailsInstance[0]?.PPO_NAME}</span> */}
					</div>
					<div className="eob-grid-item">
						<div className="category-heading">
							<FaExclamationCircle />
							<span>Claim Summary</span>
						</div>
						<div className="grid-item-fields">
							<span className="field-title">Total Amount Covered:</span>
							<span className="field-value">
								{eobDetails?.claimantSubscriberDetailsInstance.length > 0
									? eobDetails?.claimSummaryDetailsInstance[0]["SUM(D.ADJCVRD)"]
									: "N/A"}{" "}
							</span>
						</div>
						<div className="grid-item-fields">
							<span className="field-title">Paid by Insurance company:</span>
							<span className="field-value">
								{eobDetails?.claimantSubscriberDetailsInstance.length > 0
									? `$${eobDetails?.claimSummaryDetailsInstance[0]["SUM(D.COBAMT)"]}`
									: "N/A"}
							</span>
						</div>
						<div className="grid-item-fields">
							<span className="field-title">Total Paid By plans:</span>
							<span className="field-value">
								{eobDetails?.claimantSubscriberDetailsInstance.length > 0
									? `${eobDetails?.claimSummaryDetailsInstance[0]["SUM(D.PLNRESP)"]}`
									: "N/A"}
							</span>
						</div>
						<div className="grid-item-fields">
							<span className="field-title">Employee Responsibility:</span>
							<span className="field-value">
								{" "}
								${eobDetails?.claimSummaryDetailsInstance[0]["SUM(D.EERESP)"]}
							</span>
						</div>
						<div className="grid-item-fields">
							<span className="field-title">Deductable Remaining:</span>
							<span className="field-value">
								{" "}
								${eobDetails?.claimSummaryDetailsInstance[0]["SUM(D.DEDREMPLN)"]}
							</span>
						</div>
						<div className="grid-item-fields">
							<span className="field-title">In Network:</span>
							<span className="field-value">
								{" "}
								${eobDetails?.claimSummaryDetailsInstance[0]["SUM(D.DEDREMPPO)"]}
							</span>
						</div>
						<div className="grid-item-fields">
							<span className="field-title">Out Of Pocket Remaining Plan:</span>
							<span className="field-value">
								{" "}
								${eobDetails?.claimSummaryDetailsInstance[0]["SUM(D.OOPREMPLN)"]}
							</span>
						</div>
						<div className="grid-item-fields">
							<span className="field-title">Annual Accumulation:</span>
							<span className="field-value">
								{" "}
								${eobDetails?.claimSummaryDetailsInstance[0]["SUM(D.OOPREMPPO)"]}
							</span>
						</div>
					</div>
				</div>
				{/* ============================= CLAIM DETAILS ====================================*/}
				<div className="subheading-main">
					<HiDocumentReport />
					Claim Details
				</div>

				<ExplanationOfBenefitsGrid
					eligibleMembers={eobDetails?.claimDetailsInstance}
					claimTotal={eobDetails?.claimTotalInstance}
				/>

				{/* ============================= REMARKS ====================================*/}
				<div className="subheading-main">
					<TbFileReport />
					Remarks
				</div>
				<p style={{ backgroundColor: "#d9d9d9", padding: 2, width: "fit-content" }}>
					{eobDetails?.claimantSubscriberDetailsInstance[0]?.FFMSG}
				</p>
				<Remarks remarks_list={eobDetails?.query5ExplanationCodesDetailsInstance} key={0} />
			</div>
			<div style={{ display: "none" }}>
				<Suspense fallback={<div />}>
					{eobDetails && eobDetails?.claimantSubscriberDetailsInstance.length > 0 ? (
						<ExplanationOfBenefitFormPrint eobDetails={eobDetails} ref={componentRef} />
					) : null}
				</Suspense>
			</div>
		</div>
	);
};

export default ExplanationOfBenefitsV3;
